/**
 * Image Models Summary Page
 *
 * Displays all available image generation models grouped by provider,
 * showing descriptions, capabilities, and strengths for each model.
 */

import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { useImageModels } from '@/lib/hooks/useImageModelDefinition';
import { ModelDefinitionListItem } from '@/lib/api';

/** Group models by company name from capabilities */
function groupModelsByCompany(
  models: ModelDefinitionListItem[],
): { company: string; models: ModelDefinitionListItem[] }[] {
  const groups = new Map<string, ModelDefinitionListItem[]>();
  for (const model of models) {
    const company =
      ((model.capabilities as Record<string, unknown>)?.company as string) || model.provider;
    if (!groups.has(company)) groups.set(company, []);
    groups.get(company)!.push(model);
  }
  return Array.from(groups.entries()).map(([company, models]) => ({ company, models }));
}

export function ImageModelsPage() {
  const { models, loading } = useImageModels();

  const groupedModels = useMemo(() => groupModelsByCompany(models), [models]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <p className="text-sm text-muted-foreground">Loading models...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-8">
      <div className="flex items-center gap-3">
        <Link to="/a/arche_image_generator">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-xl font-semibold">Image Models</h1>
          <p className="text-sm text-muted-foreground">
            All available AI image generation models and their capabilities
          </p>
        </div>
      </div>

      {groupedModels.map((group) => (
        <section key={group.company} className="space-y-3">
          <h2 className="text-lg font-medium border-b border-border pb-1.5">{group.company}</h2>
          <div className="grid gap-3">
            {group.models.map((model) => {
              const caps = model.capabilities as Record<string, unknown>;
              return (
                <div
                  key={model.modelName}
                  className="rounded-lg border border-border bg-card p-4 space-y-1.5"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-medium text-sm">{model.displayName}</h3>
                    <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">
                      {model.modelName}
                    </span>
                  </div>
                  <p className="text-sm text-foreground/80">{model.shortDescription}</p>
                  {typeof caps?.strengths === 'string' && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Strengths:</span> {caps.strengths}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
